import Button from 'components/Buttons/Button/Button';
import { css } from 'styled-components';
import { styled } from 'components/ThemeConfigConnector/ThemeConfigConnnector';
import { Breakpoints, MQ } from 'style/styled-components/cssMediaQueries';
import { body1FontSize, body1LineHeight, fontNormal } from 'style/styled-components/cssGlobalVariables';
import { hexToRgba } from 'utils/fnColors';
import { PageTitle, PageTitleText } from 'style/styled-components/reusable.css';
import { subtitleStyles, titleStyles, visuallyHidden } from 'style/styled-components/cssHeadings';
import { PageHeading } from 'components/PageHeading/PageHeading.css';

export const PageWrapper = styled.div`
    padding: 88px 16px 0 16px;
    margin: 0 auto;

    ${MQ(Breakpoints.s)} {
        max-width: 640px;
        padding: 88px 0 0 0;
    }

    ${MQ(Breakpoints.m)} {
        padding-top: 124px;
    }

    ${PageTitle} {
        font-size: 14px;
        padding: 14px 40px;
        position: fixed;
        top: 48px;
        left: 0;
        right: 0;
        background-color: ${(props) => props.theme.brandedConfig.black_color};
        border-bottom: 1px solid ${(props) => hexToRgba(props.theme.brandedConfig.white_color, 0.1)};
        display: none;

        ${MQ(Breakpoints.m)} {
            display: block;
        }
    }

    ${PageTitleText} {
        font-family: ${fontNormal};
    }
`;

export const Title = styled.h1`
    ${titleStyles}

    @media not all and (min-width: ${Breakpoints.m}) {
        ${visuallyHidden}
    }
`;

export const Back = styled.div`
    color: var(--color-dark-body);
    font-size: 14px;
    line-height: 20px;
    font-family: ${fontNormal};
    text-align: center;
    padding-bottom: 48px;
    display: none;

    ${MQ(Breakpoints.m)} {
        display: block;
    }
`;

export const Subtitle = styled.h2<{ hideOnSmallRes?: boolean }>`
    ${subtitleStyles}

    ${(props) =>
        props.hideOnSmallRes
            ? css`
                  display: none;
                  ${MQ(Breakpoints.m)} {
                      display: block;
                  }
              `
            : css``}

    &:not(:first-child):not(${PageHeading} + &) {
        padding-top: ${(props) => (props.hideOnSmallRes ? '0' : '40px')};
    }
`;

export const Section = styled.section<{ hideOnSmallRes?: boolean }>`
    &:not(:first-child):not(${PageHeading} + &) {
        padding-top: ${(props) => (props.hideOnSmallRes ? '0' : '40px')};

        ${(props) =>
            props.hideOnSmallRes
                ? css`
                      ${MQ(Breakpoints.m)} {
                          padding-top: 40px;
                      }
                  `
                : css``}
    }
`;

export const OptionRightSide = styled.div`
    margin-left: auto;
`;

export const Option = styled.div<{ reducedHeight?: boolean; clickable?: boolean; isDisabled?: boolean }>`
    color: var(--color-dark-body);
    font-size: 16px;
    line-height: 24px;
    font-family: ${fontNormal};
    background-color: var(--color-dark-foreground);
    border-radius: 4px;
    margin-bottom: 8px;
    position: relative;
    display: flex;
    align-items: center;

    &:focus-visible {
        outline: 2px solid white;
    }

    ${(props) =>
        props.clickable
            ? css`
                  cursor: pointer;
              `
            : ``}

    ${(props) =>
        props.reducedHeight
            ? css`
                  padding: 10px 23px 10px 18px;
              `
            : css`
                  padding: 18px 23px 18px 18px;
              `}
    ${(props) =>
        props.isDisabled
            ? css`
                  opacity: 0.5;
                  ${OptionRightSide} {
                      display: none;
                  }
              `
            : ''};
`;

export const OptionCheckbox = styled.label`
    color: color: var(--color-dark-body);;
    font-size: 16px;
    line-height: 24px;
    font-family: ${fontNormal};
    background-color: ${(props) => props.theme.brandedConfig.low_contrast};
    border-radius: 4px;
    margin-bottom: 8px;
    position: relative;
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 14px 23px 18px 14px;
`;

export const OptionIcon = styled.div`
    margin-right: 16px;

    svg {
        display: block;
        width: 24px;
        height: 100%;

        path {
            fill: var(--color-dark-body);
        }
    }
`;

export const OptionName = styled.div``;

export const OptionNameSub = styled.div`
    color: var(--color-dark-body-70);
    font-size: 14px;
    line-height: 20px;
    clear: both;
    display: none;

    ${MQ(Breakpoints.m)} {
        display: block;
    }
`;

export const OptionButton = styled.a<{ isExternal?: boolean }>`
    font-family: ${fontNormal};
    display: block;
    cursor: pointer;

    svg {
        display: block;
        width: ${(props) => (props.isExternal ? '20px' : '7px')};
        height: 100%;

        path {
            fill: var(--color-dark-body);
        }
    }
`;

export const AboutInfo = styled.p`
    opacity: 0.7;
`;

export const DescriptionTitle = styled.p`
    font-size: 16px;
    line-height: 24px;
    font-family: ${fontNormal};
    padding-bottom: 16px;
`;

export const StyledButton = styled(Button)`
    min-width: 100%;
    text-align: center;

    ${MQ(Breakpoints.xs)} {
        min-width: 160px;
    }
`;

export const ButtonsContainer = styled.div`
    text-align: center;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;

    ${MQ(Breakpoints.s)} {
        position: static;
    }
`;

export const CenterButton = styled(Button)<{ isDisabled?: boolean }>`
    position: fixed;
    bottom: 70px;
    left: 0;
    right: 0;
    margin-left: 13px;
    margin-right: 13px;

    ${MQ(Breakpoints.xs)} {
        position: unset;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: 22px;
    }
`;

export const ErrorMessage = styled.p`
    text-align: center;
    font-family: ${fontNormal};
    font-size: ${body1FontSize};
    line-height: ${body1LineHeight};

    ${MQ(Breakpoints.xs)} {
        margin-top: 38px;
    }
`;

export const AriaHint = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`;
